import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAccount, useChainId } from 'wagmi';
import WalletConnect from './WalletConnect';
import { useDisconnect } from 'wagmi';
import { SUPPORTED_CHAINS } from '../utils/relayProtocol';
import WalletPromptModal from './shared/modals/WalletPromptModal';
import { ZNSAddressDisplay } from './shared/ZNSAddressDisplay';
import { ZNSRegistrationModal } from './shared/ZNSRegistrationModal';
import { Search, Menu, X } from 'lucide-react';
import PriceFooter from './shared/PriceFooter';
import PortfolioPreview from './discover/PortfolioPreview';
import CustomSwapWidget from './swap/CustomSwapWidget';

// ============================================
// BEHOLDER-INSPIRED NAVIGATION DESIGN
// Clean, minimal, professional
// ============================================

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: var(--color-bg, #000000);
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const NavigationBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: #000000;
  border-bottom: 1px solid var(--color-border, #333333);
`;

const NavContainer = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
    height: 56px;
    gap: 16px;
  }
`;

const LogoSection = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  flex-shrink: 0;
`;

const Logo = styled.img`
  height: 32px;
  width: auto;
  
  @media (max-width: 768px) {
    height: 28px;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '500'};
  color: ${props => props.$active ? '#ffffff' : '#a1a1aa'};
  text-decoration: none;
  border-radius: 8px;
  transition: all 150ms ease;
  white-space: nowrap;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
  
  ${props => props.$active && `
    background: rgba(255, 255, 255, 0.1);
  `}
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const SearchButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 8px;
  color: #a1a1aa;
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    border-color: var(--color-border-light, #2a2a2a);
    color: #ffffff;
    background: var(--color-bg-hover, #000000);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ConnectButton = styled.button`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  background: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  white-space: nowrap;
  
  &:hover {
    background: #e4e4e7;
  }
  
  @media (max-width: 768px) {
    padding: 6px 14px;
    font-size: 13px;
  }
`;

const WalletBadge = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  background: var(--color-bg-elevated, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: var(--color-bg-hover, #000000);
    border-color: var(--color-border-light, #2a2a2a);
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const ChainIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-elevated, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 8px;
  color: #ffffff;
  cursor: pointer;
  
  @media (max-width: 900px) {
    display: flex;
  }
`;

// Mobile Menu
const MobileMenuOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 200;
  opacity: ${props => props.$open ? 1 : 0};
  pointer-events: ${props => props.$open ? 'auto' : 'none'};
  transition: opacity 200ms ease;
`;

const MobileMenuPanel = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  max-width: 85vw;
  background: var(--color-bg-elevated, #000000);
  border-left: 1px solid var(--color-border, #333333);
  z-index: 201;
  transform: ${props => props.$open ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border, #333333);
`;

const MobileMenuClose = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #a1a1aa;
  cursor: pointer;
  
  &:hover {
    color: #ffffff;
  }
`;

const MobileMenuLinks = styled.nav`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
`;

const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  padding: 14px 16px;
  font-size: 15px;
  font-weight: ${props => props.$active ? '600' : '500'};
  color: ${props => props.$active ? '#ffffff' : '#a1a1aa'};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  text-decoration: none;
  border-radius: 8px;
  transition: all 150ms ease;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const MobileMenuFooter = styled.div`
  padding: 20px;
  border-top: 1px solid var(--color-border, #333333);
`;

// Main Content Wrapper - contains page content and optional sidebar
const ContentWrapper = styled.div<{ $hasSidebar: boolean }>`
  flex: 1;
  display: flex;
  min-height: calc(100vh - 64px);
  padding-bottom: 56px; /* Space for fixed footer */
  
  @media (max-width: 768px) {
    padding-bottom: 52px;
  }
  
  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

// Main Content - left side with page content
const MainContent = styled.main<{ $hasSidebar: boolean }>`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  
  ${props => !props.$hasSidebar && `
    max-width: 100%;
  `}
`;

// Right Sidebar - Portfolio + Swap
const RightSidebar = styled.aside`
  width: 380px;
  min-width: 380px;
  border-left: 1px solid var(--color-border, #333333);
  background: var(--color-bg, #000000);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 64px - 56px);
  position: sticky;
  top: 64px;
  
  @media (max-width: 1200px) {
    width: 100%;
    min-width: 100%;
    border-left: none;
    border-top: 1px solid var(--color-border, #333333);
    max-height: none;
    position: relative;
    top: 0;
    flex-direction: row;
    flex-wrap: wrap;
    
    & > * {
      flex: 1;
      min-width: 300px;
    }
  }
  
  @media (max-width: 700px) {
    padding: 16px;
    flex-direction: column;
    
    & > * {
      min-width: 100%;
    }
  }
`;

// Navigation items configuration
const NAV_ITEMS = [
  { path: '/create', label: 'Create' },
  { path: '/explore', label: 'Explore' },
  { path: '/trade', label: 'Trade' },
  { path: '/locks', label: 'Locks & TVL' },
  { path: '/tydro', label: 'Borrow' },
  { path: '/profile', label: 'Portfolio' },
];

const DashboardLayout: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showWalletPrompt, setShowWalletPrompt] = useState(false);
  const [showZNSModal, setShowZNSModal] = useState(false);
  const { isConnected, address } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  const location = useLocation();

  // Get current chain logo
  const getCurrentChainLogo = () => {
    const chain = Object.values(SUPPORTED_CHAINS).find(chain => chain.chainId === chainId);
    return chain?.logo || SUPPORTED_CHAINS.INK.logo;
  };

  // Show wallet prompt on first visit if not connected
  useEffect(() => {
    const hasSeenPrompt = localStorage.getItem('mavrk_wallet_prompt_seen');
    if (!isConnected && !hasSeenPrompt) {
      const timer = setTimeout(() => setShowWalletPrompt(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isConnected]);

  const handleWalletPromptClose = () => {
    setShowWalletPrompt(false);
    localStorage.setItem('mavrk_wallet_prompt_seen', 'true');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Check if path is active
  const isActive = (path: string) => {
    if (path === '/explore') {
      return location.pathname === '/explore' || location.pathname.startsWith('/token/');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  // Check if we're on the Create page (hide sidebar there)
  const isCreatePage = location.pathname === '/create';

  return (
    <DashboardContainer>
      {/* Navigation Bar */}
      <NavigationBar>
        <NavContainer>
          {/* Logo */}
          <LogoSection to="/explore">
            <Logo src="/landing-assets/mavrk_word_logo.png" alt="Mavrk" />
          </LogoSection>

          {/* Desktop Navigation Links */}
          <NavLinks>
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                $active={isActive(item.path)}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>

          {/* Right Section */}
          <RightSection>
            <SearchButton aria-label="Search">
              <Search size={18} />
            </SearchButton>
            
            {/* Always show WalletConnect for unified wallet management (EVM + Solana) */}
            <WalletConnect />
            
            {/* Show chain icon when connected */}
            {isConnected && (
              <ChainIcon src={getCurrentChainLogo()} alt="Chain" />
            )}
            
            <MobileMenuButton onClick={() => setMobileMenuOpen(true)} aria-label="Menu">
              <Menu size={20} />
            </MobileMenuButton>
          </RightSection>
        </NavContainer>
      </NavigationBar>

      {/* Mobile Menu */}
      <MobileMenuOverlay $open={mobileMenuOpen} onClick={closeMobileMenu} />
      <MobileMenuPanel $open={mobileMenuOpen}>
        <MobileMenuHeader>
          <Logo src="/landing-assets/mavrk_word_logo.png" alt="Mavrk" style={{ height: '24px' }} />
          <MobileMenuClose onClick={closeMobileMenu}>
            <X size={24} />
          </MobileMenuClose>
        </MobileMenuHeader>
        
        <MobileMenuLinks>
          {NAV_ITEMS.map(item => (
            <MobileNavLink
              key={item.path}
              to={item.path}
              $active={isActive(item.path)}
              onClick={closeMobileMenu}
            >
              {item.label}
            </MobileNavLink>
          ))}
        </MobileMenuLinks>
        
        <MobileMenuFooter>
          <WalletConnect />
        </MobileMenuFooter>
      </MobileMenuPanel>

      {/* Main Content with Optional Sidebar */}
      <ContentWrapper $hasSidebar={!isCreatePage}>
        <MainContent $hasSidebar={!isCreatePage}>
          <Outlet />
        </MainContent>
        
        {/* Right Sidebar - Portfolio + Swap (not shown on Create page) */}
        {!isCreatePage && (
          <RightSidebar>
            <PortfolioPreview />
            <CustomSwapWidget />
          </RightSidebar>
        )}
      </ContentWrapper>

      {/* Wallet Prompt Modal */}
      {showWalletPrompt && (
        <WalletPromptModal onClose={handleWalletPromptClose} />
      )}

      {/* ZNS Registration Modal */}
      <ZNSRegistrationModal
        isVisible={showZNSModal}
        onClose={() => setShowZNSModal(false)}
      />

      {/* Global Footer - ETH Price, Social Links, Token Search */}
      <PriceFooter />
    </DashboardContainer>
  );
};

export default DashboardLayout;
